/**
 * Upload a certificate, check what we read off it, save.
 *
 * This is the design's scan flow with the camera taken out. The mockup's
 * three phases — capture, scanning, result — become upload, reading, confirm,
 * and the last one is the same screen it always was: a form of what the
 * document says, for a person to accept.
 *
 * What changed is honesty about the middle step. The mockup's OCR always
 * "succeeded", because it was a `setTimeout` returning invented fields. A real
 * read often comes back with two of four fields, or none, so the form
 * distinguishes three states — read it, could not read it, did not try — and
 * never pre-fills a box with a guess.
 */
import { AlertTriangle, FileUp, Loader2, Sparkles, Upload } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import type { CertificateSuggestion, DocType } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import { cn } from "@/lib/utils";
import { useCertificateScan, type ScanUpload } from "./crew-api";

export type CertificateUploadProps = {
  personId: string;
  personName: string;
  type: DocType;
  label: string;
  /** Pre-fills the issuing body when the jurisdiction names one for this type. */
  suggestedBody?: string;
  onSaved: (input: {
    documentId: string;
    issuingBody: string;
    reference?: string;
    issuedDate?: string;
    expiryDate: string;
  }) => Promise<void>;
  onCancel: () => void;
};

export function CertificateUpload({
  personId,
  personName,
  type,
  label,
  suggestedBody,
  onSaved,
  onCancel,
}: CertificateUploadProps) {
  const { state, upload, reset } = useCertificateScan(personId);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="font-display text-[1.05rem] font-bold uppercase tracking-[0.01em] text-ink">
          {label}
        </div>
        <p className="mt-1 text-[0.84rem] leading-[1.5] text-muted">
          Upload a photo or PDF of {personName}&rsquo;s card. We&rsquo;ll read what we
          can and you confirm it before it counts.
        </p>
      </div>

      {state.phase === "idle" && (
        <>
          {/* The dashed border was doing the whole job of promising this
              accepted a dropped file, and nothing listened for one — the box
              was a styled `button` with a click handler, so a dropped card
              navigated the tab to the file instead. The affordance was the
              honest part; the missing half was the handlers.

              Drag depth is a ref rather than a boolean because `dragleave`
              fires when the pointer crosses into a child, which flickers the
              highlight off over the icon and the labels. Same reasoning, and
              the same fix, as the journal's photo drop. */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              dragDepth.current += 1;
              setDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              dragDepth.current -= 1;
              if (dragDepth.current <= 0) setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              dragDepth.current = 0;
              setDragging(false);
              // One card at a time: `useCertificateScan` reads a single file
              // against a single requirement, so a multi-file drop takes the
              // first rather than silently discarding the rest of a batch the
              // user thought had been accepted.
              const file = e.dataTransfer.files?.[0];
              if (file) void upload(type, file);
            }}
            className={cn(
              "flex cursor-pointer flex-col items-center gap-2 rounded-card border-2 border-dashed",
              "px-5 py-8 text-center transition-colors",
              dragging
                ? "border-brand bg-brand-tint-3"
                : "border-border bg-surface-sunken hover:border-brand hover:bg-brand-tint-3",
            )}
          >
            <span className="flex text-muted">
              <Upload size={24} aria-hidden />
            </span>
            <span className="text-[0.95rem] font-bold text-ink">
              {dragging ? "Drop to upload" : "Choose a file"}
            </span>
            <span className="text-[0.82rem] text-muted">
              A photo of the card, or a PDF certificate
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            // Photos and PDFs, because that is what a certificate arrives as.
            // The server's allow-list is still the real rule.
            accept="image/png,image/jpeg,application/pdf"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset, so picking the same file twice in a row still fires.
              e.target.value = "";
              if (file) void upload(type, file);
            }}
          />
          <Button variant="secondary" size="footer" onClick={onCancel}>
            Cancel
          </Button>
        </>
      )}

      {(state.phase === "uploading" || state.phase === "reading") && (
        <Progress state={state} />
      )}

      {state.phase === "error" && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2.5 rounded-card border-[1.5px] border-danger/40 bg-danger-bg px-4 py-3">
            <span className="mt-0.5 flex shrink-0 text-danger">
              <AlertTriangle size={17} aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-[0.9rem] font-bold text-ink">
                That didn&rsquo;t upload
              </div>
              <p className="mt-0.5 text-[0.84rem] text-muted">{state.message}</p>
            </div>
          </div>
          <div className="flex gap-2.5">
            {/* A refused file would be refused again (`architecture/0031`). */}
            {!state.refused && (
              <Button
                size="footer"
                className="flex-1"
                onClick={() => void upload(type, state.file)}
              >
                Try again
              </Button>
            )}
            <Button variant="secondary" size="footer" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {state.phase === "ready" && (
        <ConfirmForm
          filename={state.filename}
          suggestion={state.suggestion}
          suggestedBody={suggestedBody}
          onBack={reset}
          onSave={(values) => onSaved({ documentId: state.documentId, ...values })}
        />
      )}
    </div>
  );
}

function Progress({
  state,
}: {
  state: Extract<ScanUpload, { phase: "uploading" | "reading" }>;
}) {
  const reading = state.phase === "reading";
  return (
    <div className="rounded-card border-[1.5px] border-border bg-surface px-4 py-4">
      <div className="flex items-center gap-2.5">
        <span className="flex shrink-0 text-brand">
          {reading ? (
            <Sparkles size={17} aria-hidden />
          ) : (
            <FileUp size={17} aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.9rem] font-semibold text-ink">
          {state.filename}
        </span>
        <span className="flex shrink-0 text-muted">
          <Loader2 size={16} className="animate-spin" aria-hidden />
        </span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-subtle">
        <div
          className={cn(
            "h-full rounded-full bg-brand transition-[width] duration-200",
            reading && "animate-pulse",
          )}
          style={{ width: reading ? "100%" : `${state.progress}%` }}
        />
      </div>
      <p className="mt-2 text-[0.82rem] text-muted">
        {reading ? "Reading the card…" : "Uploading…"}
      </p>
    </div>
  );
}

type ConfirmValues = {
  issuingBody: string;
  reference?: string;
  issuedDate?: string;
  expiryDate: string;
};

function ConfirmForm({
  filename,
  suggestion,
  suggestedBody,
  onBack,
  onSave,
}: {
  filename: string;
  suggestion: CertificateSuggestion;
  suggestedBody?: string;
  onBack: () => void;
  onSave: (values: ConfirmValues) => Promise<void>;
}) {
  // The issuing body falls back to the jurisdiction's known body for this
  // document type — a fact about the certificate, not a guess about this one.
  // The expiry never falls back to anything.
  const [issuingBody, setIssuingBody] = useState(
    suggestion.issuingBody ?? suggestedBody ?? "",
  );
  const [reference, setReference] = useState(suggestion.reference ?? "");
  const [issuedDate, setIssuedDate] = useState(suggestion.issuedDate ?? "");
  const [expiryDate, setExpiryDate] = useState(suggestion.expiryDate ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const readAnything =
    suggestion.issuingBody !== null ||
    suggestion.reference !== null ||
    suggestion.expiryDate !== null;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!issuingBody.trim()) {
      setError("Who issued this certificate?");
      return;
    }
    if (!expiryDate) {
      setError("An expiry date is required — it is what every flag is based on.");
      return;
    }
    if (issuedDate && expiryDate < issuedDate) {
      setError("The expiry date is before the issue date.");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        issuingBody: issuingBody.trim(),
        reference: reference.trim() || undefined,
        issuedDate: issuedDate || undefined,
        expiryDate,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save that.");
      setSaving(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
      <div
        className={cn(
          "flex items-start gap-2.5 rounded-card border-[1.5px] px-4 py-3",
          readAnything
            ? "border-brand-border bg-brand-tint-2"
            : "border-border bg-surface-sunken",
        )}
      >
        <span
          className={cn("mt-0.5 flex shrink-0", readAnything ? "text-brand" : "text-muted")}
        >
          <Sparkles size={16} aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[0.88rem] font-bold text-ink">{filename}</div>
          <p className="mt-0.5 text-[0.82rem] leading-[1.45] text-muted">
            {!suggestion.attempted
              ? "Uploaded. Reading cards automatically isn’t switched on here, so fill these in yourself."
              : readAnything
                ? "We filled in what we could read. Check every field against the card before saving."
                : "Uploaded, but we couldn’t read anything off it. Fill these in from the card."}
          </p>
        </div>
      </div>

      <Field label="Issuing body">
        {(props) => (
          <Input
            {...props}
            value={issuingBody}
            onChange={(e) => setIssuingBody(e.target.value)}
            placeholder="CITB, IPAF, RoSPA…"
          />
        )}
      </Field>

      <Field label="Reference" optional>
        {(props) => (
          <Input
            {...props}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Card or certificate number"
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Issued" optional>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={issuedDate}
              onChange={(e) => setIssuedDate(e.target.value)}
            />
          )}
        </Field>
        <Field label="Expires">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          )}
        </Field>
      </div>

      {error !== null && (
        <p role="alert" className="text-[0.84rem] font-semibold text-danger">
          {error}
        </p>
      )}

      <div className="flex gap-2.5">
        <Button type="submit" size="footer" className="flex-1" disabled={saving}>
          {saving ? "Saving…" : "Save certificate"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="footer"
          onClick={onBack}
          disabled={saving}
        >
          Replace file
        </Button>
      </div>
    </form>
  );
}
